import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core'
import { Store, select } from '@ngrx/store'
import {
  Destroyable,
  PromptDialogService,
  Rental,
  Toaster,
  selectRentalEntities,
  untilDestroy,
  SaveForm,
  DialogService,
} from '@tokeet-frontend/tv3-platform'
import { ConnectionView } from '@tv3/store/connection/connection.view'
import * as lodash from 'lodash'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { delay, finalize, switchMap } from 'rxjs/operators'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'
import { combineLatest } from 'rxjs'
import {
  ChannelReviewItem,
  ChannelReviewSearchParams,
  getChannelMaxRating,
  ReviewService,
} from '@tokeet-frontend/reviews'
import { FormControl, Validators } from '@angular/forms'
import { ReviewsDialogComponent } from '../reviews-dialog/reviews-dialog.component'
import { PageEvent } from '@angular/material/paginator'

@Component({
  selector: 'app-recent-reviews',
  templateUrl: './recent-reviews.component.html',
  styleUrls: ['./recent-reviews.component.scss'],
})
export class RecentReviewsComponent extends Destroyable implements OnInit, OnChanges {
  @Input() title = $localize`Latest Reviews`
  @Input() pager = false
  @Input() showHeader = true
  @Input() canSeeReply = false
  @Input() repliable = false
  @Input() filters: ChannelReviewSearchParams

  get mergeReviews() {
    return !this.pager
  }

  isLoading = true
  reviews: ChannelReviewItem[] = []
  totalCount = 0
  limit = 10
  pageIndex = 1
  emptyType = TableType.Reviews
  rentalsById: Record<string, Rental> = {}
  currentEpoch = Date.now() / 1000
  get isAllCollapsed() {
    return this.collapsedReviewIds.size === this.reviews.length
  }
  collapsedReviewIds = new Set<string>()

  replyCtrl = new FormControl('', [Validators.required])
  isReplyingReviewId: string

  constructor(
    private store: Store<any>,
    private toaster: Toaster,
    private reviewService: ReviewService,
    private dialog: DialogService
  ) {
    super()
  }

  ngOnInit(): void {
    this.store.pipe(select(selectRentalEntities), untilDestroy(this)).subscribe((items) => {
      this.rentalsById = items
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters']?.currentValue) {
      this.reviews = []
      this.pageIndex = 1
      this.refresh()
    }
  }

  getMaxRating(item: ChannelReviewItem) {
    return getChannelMaxRating(item.source)
  }

  onToggleCollapseAll() {
    if (this.isAllCollapsed) {
      this.collapsedReviewIds.clear()
    } else {
      this.collapsedReviewIds = new Set(this.reviews.map((t) => t.reviewId))
    }
  }

  onCollapseReview(item: ChannelReviewItem) {
    if (this.collapsedReviewIds.has(item.reviewId)) {
      this.collapsedReviewIds.delete(item.reviewId)
    } else {
      this.collapsedReviewIds.add(item.reviewId)
    }
  }

  refresh() {
    this.isLoading = true
    combineLatest([
      this.reviewService.searchReviews({
        ...this.filters,
        includeStats: false,
        page: this.pageIndex,
        limit: this.limit,
      }),
    ])
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        ([data]) => {
          this.reviews = lodash
            .sortBy(
              this.mergeReviews ? lodash.uniqBy([...data.reviews, ...this.reviews], (t) => t.reviewId) : data.reviews,
              (t) => t.submittedAt
            )
            .reverse()
          this.totalCount = data?.counts?.total || 0
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onLoadMore() {
    this.pageIndex = this.pageIndex + 1
    this.refresh()
  }

  onPageChange($event: PageEvent) {
    this.pageIndex = $event.pageIndex + 1 // zero base
    this.limit = $event.pageSize
    this.refresh()
  }

  onAllReviews() {
    this.dialog.openVariableDialog(ReviewsDialogComponent, { width: '80%', height: 'auto' })
  }

  getProperty(properties: ConnectionView[], listingId: string) {
    return lodash.find(properties, (t) => t.roomId === listingId)
  }

  canReply(review: ChannelReviewItem) {
    return review.reviewer?.role != 'host' && !review.response && review.isEligibleForResponse
  }

  @SaveForm()
  onReply(ctrl: FormControl, review: ChannelReviewItem) {
    const response = lodash.trim(ctrl.value)
    if (!response) return

    this.reviewService
      .respondReview(review._id, response)
      .pipe(switchMap((response) => this.reviewService.getReview(review._id)))
      .subscribe(
        (review) => {
          this.reviews = lodash.map(this.reviews, (t) => (t.reviewId === review.reviewId ? review : t))
          this.toaster.success($localize`Review replied successfully`)
          this.isReplyingReviewId = null
          this.replyCtrl.reset()
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }
}
